<template>
    <div class="icon-carousel">
        <div class="carousel">
            <div
                ref="container"
                class="slides"
            >
                <div
                    v-for="(item, index) in data.items"
                    :key="index"
                    ref="elements"
                    class="slide"
                >
                    <CmsLink
                        v-if="item.itemLink"
                        :link="item.itemLink"
                        class="item"
                    >
                        <div class="icon">
                            <Icon :icon="cmsIconToThIcon(item.icon)" />
                        </div>

                        <div class="text">
                            {{ item.text }}
                        </div>
                    </CmsLink>

                    <div
                        v-else
                        class="item"
                    >
                        <div class="icon">
                            <Icon :icon="cmsIconToThIcon(item.icon)" />
                        </div>

                        <div class="text">
                            {{ item.text }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <ButtonRound
            :disabled="isAtStart"
            class="left"
            icon="angle-left"
            :aria-label="t('catalogue.carousel.button_prev')"
            @click="previous"
        />

        <ButtonRound
            :disabled="isAtEnd"
            class="right"
            icon="angle-right"
            :aria-label="t('catalogue.carousel.button_next')"
            @click="next"
        />

        <div class="carousel-pagination">
            <div
                v-for="index in pageCount"
                :key="index"
                :class="getClassDot(index)"
                class="carousel-page"
                @click.prevent.stop="goToPage(index)"
            ></div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Color, ComponentIconCarouselFragment } from '@/graphql/api'

import { useCarouselDotsPage } from '@/components/layout/carousels/base/useCarouselDotsPage'
import { cmsIconToThIcon } from '@/helpers/icon'
import type { Colors } from '@/components/ui'
import { getColorForBackground } from '@/components/ui/buttons'

import { useCarousel } from '@/components/layout/carousels/base/useCarouselNew'
import { cmsColorToButtonColor } from '@/components/cms'

import Icon from '@/components/ui/icon/Icon.vue'
import ButtonRound from '@/components/ui/buttons/ButtonRound.vue'
import CmsLink from '@/components/cms/CmsLink.vue'

const { data, background } = defineProps<{
    data: ComponentIconCarouselFragment
    background: Color
}>()

const { t } = useI18n()

const thColor = computed(
    (): Colors | undefined =>
        background && getColorForBackground(cmsColorToButtonColor(background))
)

const {
    container,
    elements,
    slideWidth,
    previous,
    next,
    current,
    isAtStart,
    isAtEnd,
} = useCarousel(toRef(() => data.items))

const { pageCount, getClassDot, goToPage } = useCarouselDotsPage(
    container,
    slideWidth,
    data.items,
    current
)
</script>

<style lang="scss" scoped>
.icon-carousel {
    position: relative;
    margin: auto;
    z-index: 10;
    max-width: 1440px;
    color: v-bind(thColor);
    padding: 40px 0;

    @include hover() {
        z-index: 11;
    }

    .carousel {
        width: 1040px;
        max-width: 100%;
        height: auto;
        margin: 0 auto;

        .slides {
            position: relative;
            display: flex;
            overflow-y: hidden;
            overflow-x: scroll;
            scroll-snap-type: x mandatory;
            scroll-padding: 0 40px;
            padding: 0 40px;
            -ms-overflow-style: none; /* IE and Edge */
            scrollbar-width: none; /* Firefox */

            &::-webkit-scrollbar {
                display: none;
            }

            &::after {
                content: '';
                padding-right: 40px;

                @include min-width(1024) {
                    padding-right: 0;
                }
            }

            @include min-width(1360) {
                padding: 0;
                scroll-padding: 0;
            }

            .slide {
                position: relative;
                user-select: none;
                scroll-snap-align: start;
                width: 100%;
                flex-shrink: 0;

                @include min-width(768) {
                    width: 25%;
                }

                .item {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    flex-direction: column;
                    gap: 20px;

                    .icon {
                        width: 100px;
                        height: 100px;
                        flex-grow: 0;
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 48px;
                    }

                    .text {
                        padding: 0 20px 20px;
                        width: 100%;
                        text-align: center;
                        font-size: 1.25rem;
                        line-height: 1.4em;
                        font-family: $parcel-font-stack;
                        margin-bottom: auto;

                        @include min-width(1024) {
                            padding: 0 40px 20px;
                        }
                    }
                }
            }
        }
    }

    .button-round {
        position: absolute;
        z-index: 3;
        top: calc(50% - 50px);
        border: 1px solid v-bind(thColor);
        color: v-bind(thColor);
        background: transparent;

        &.disabled {
            opacity: 0;
        }

        &.left {
            left: 15px;

            @include min-width(1600) {
                left: -20px;
            }
        }

        &.right {
            right: 15px;

            @include min-width(1600) {
                right: -20px;
            }
        }
    }

    .carousel-pagination {
        display: flex;
        justify-content: center;
        padding: 40px 0 0;

        .carousel-page {
            flex: 0 0 7px;
            height: 7px;
            border: 1px solid v-bind(thColor);
            margin: 4px;
            border-radius: 50%;
            cursor: pointer;
            transition: background-color 80ms ease-in-out;
            opacity: 0.3;

            &.active {
                background-color: v-bind(thColor);
                opacity: 1;
            }
        }
    }
}
</style>
